import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cvAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { 
  FileText, Plus, Loader2, LogOut, Trash2, Edit, Calendar, Globe, Search, Shield, FlaskConical
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const TEMPLATE_NAMES = {
  t1: 'Navy Sidebar + Gold',
  t2: 'Monogram Circle',
  t3: 'Top Header Rail',
  t4: 'Photo Card Timeline',
  t5: 'Dark Photo Sidebar',
  t6: 'Dark Sidebar (No Photo)',
};

const Dashboard = () => {
  const { user, logout, isSuperadmin } = useAuth();
  const navigate = useNavigate();

  const [cvList, setCvList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchCVs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await cvAPI.list(debouncedSearch);
      setCvList(data);
    } catch (err) {
      console.error('Failed to fetch CVs:', err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    fetchCVs();
  }, [fetchCVs]);

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await cvAPI.delete(deleteId);
      setCvList(prev => prev.filter(cv => cv.id !== deleteId));
    } catch (err) {
      console.error('Failed to delete CV:', err);
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50/50 flex flex-col" data-testid="dashboard-page">
      {/* Header */}
      <header className="h-14 md:h-16 border-b border-zinc-200 bg-white flex items-center justify-between px-4 md:px-6 shrink-0">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-8 h-8 md:w-10 md:h-10 bg-zinc-900 rounded-lg flex items-center justify-center">
            <FileText className="w-4 h-4 md:w-5 md:h-5 text-white" />
          </div>
          <span className="font-outfit font-semibold text-base md:text-lg">MoCV Studio</span>
        </div>
        <div className="flex items-center gap-2 md:gap-4">
          <span className="text-xs md:text-sm text-zinc-500 hidden sm:inline">{user?.name}</span>
          {isSuperadmin() && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/users')}
              data-testid="admin-panel-btn"
              className="border-indigo-500 text-indigo-600 hover:bg-indigo-50 px-2 md:px-3"
            >
              <Shield className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Admin</span>
            </Button>
          )}
          {isSuperadmin() && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/template-qa/v3')}
              data-testid="admin-qa-btn"
              className="border-purple-500 text-purple-600 hover:bg-purple-50 px-2 md:px-3"
            >
              <FlaskConical className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Template QA</span>
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            data-testid="logout-btn"
            className="px-2 md:px-3"
          >
            <LogOut className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Sign Out</span>
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-8 overflow-auto">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="flex flex-col gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-zinc-900">
                My CVs
              </h1>
              <p className="text-zinc-500 mt-1 text-sm md:text-base">
                Manage all your created CVs
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              {/* Search Input */}
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  placeholder="Search by name or title..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-full sm:w-64"
                  data-testid="search-input"
                />
              </div>
              <Button
                onClick={() => navigate('/generate')}
                className="bg-zinc-900 hover:bg-zinc-800 w-full sm:w-auto"
                data-testid="create-new-cv-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New CV
              </Button>
              <Button
                onClick={() => navigate('/cv-builder-v2')}
                variant="outline"
                className="border-blue-500 text-blue-600 hover:bg-blue-50 w-full sm:w-auto"
                data-testid="cv-builder-v2-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                CV Builder V2
              </Button>
              <Button
                onClick={() => navigate('/cv-builder-v3')}
                variant="outline"
                className="border-purple-500 text-purple-600 hover:bg-purple-50 w-full sm:w-auto"
                data-testid="cv-builder-v3-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                CV Builder V3 ✨
              </Button>
            </div>
          </div>

          {/* CV List */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
            </div>
          ) : cvList.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-20">
                <FileText className="w-12 h-12 text-zinc-300 mb-4" />
                <h3 className="font-medium text-zinc-700 mb-2">
                  {searchQuery ? 'No CVs found' : 'No CVs yet'}
                </h3>
                <p className="text-sm text-zinc-500 mb-4">
                  {searchQuery ? 'Try a different search term' : 'Create your first CV to get started'}
                </p>
                {!searchQuery && (
                  <Button onClick={() => navigate('/generate')} data-testid="create-first-cv-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Create CV
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {cvList.map((cv) => (
                <Card 
                  key={cv.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer group"
                  data-testid={`cv-card-${cv.id}`}
                  onClick={() => navigate(`/edit/${cv.id}`)}
                >
                  <CardContent className="p-4 md:p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-zinc-900 truncate">
                          {cv.candidateName}
                        </h3>
                        {cv.candidateTitle && (
                          <p className="text-sm text-zinc-500 truncate">{cv.candidateTitle}</p>
                        )}
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/edit/${cv.id}`);
                          }}
                          data-testid={`edit-cv-${cv.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(cv.id);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-cv-${cv.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 text-xs text-zinc-400">
                      <div className="flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        {cv.language === 'en' ? 'English' : 'French'}
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(cv.updatedAt).toLocaleDateString()}
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-zinc-100">
                      <span className="text-xs font-medium text-zinc-500">
                        {TEMPLATE_NAMES[cv.templateId] || cv.templateId}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete CV?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the CV and all its versions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Dashboard;
